<script lang="ts" setup>
import { ref, onMounted, reactive } from "vue";
import AUApi from "@/api";
import {
  getKennungValueDisplay,
  getKennungValue,
  getKennungTitle,
} from "@/DataHelper";
import { getSiteData } from "@/NavigationHelper";
import { Globals } from "@/Globals";
import { globalsStt } from "@/local";
import { mountedBase, onSave, BaseSetup, onDelete } from "@/DataLoaderStatic";
import { goBack } from "@/NavigationHelper";
import { showSnackbar } from "@/UIHelper";

import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
const validator = createEditorValidator();
provideEditorValidator(validator);

let showDeleteMask = ref<boolean>(false);
let selectFahrzeug = ref<boolean>(false);
let fahrtkosten = ref<boolean>(false);
let showAbfahrt = ref<boolean>(false);
let showKennzeichen = ref<boolean>(false);
let km = ref<number>(0);
let abfahrt = ref<number>(0);
let ankunft = ref<number>(0);
let componentKey = ref<number>(0);
let setCompKey = ref<number>(0);

const emit = defineEmits(["back", "save"]);
const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_RK_NewKMGeldErfassung",
  aic: AUApi.Current.user.aic,
  emitBack: () => emit("back"),
};

const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
onMounted(async () => {
  const siteData = getSiteData();
  pageParams.bewegungsdaten = false;
  if (siteData?.bewegungsdaten) {
    pageParams.bewegungsdaten = true;
    pageParams.aic = siteData.aicBewegung;
  } else {
    if (siteData?.aicReise) {
      pageParams.aic = siteData.aicReise;
    }
  }
  const Stamm = globalsStt(Globals.Stt, 0, "RK_TRAVEL");
  if (siteData && siteData.aicReise && Stamm) {
    if (!pageParams.jokerStt) {
      pageParams.jokerStt = [];
    }
    pageParams.jokerStt.push({
      sync: siteData.aicReise, // aic Stammsatz
      aic: Stamm.aic, // aic Stammtyp
    });
  }
  if (siteData?.showDelete) {
    showDeleteMask.value = siteData.showDelete;
  }
  await mountedBase(uiData, pageParams, false);
  if (!uiData.showLoading && uiData.data) {
    const row = uiData.data[0];
    showAbfahrt.value = getKennungTitle("RK_KILOMETER", uiData.columns);
    showKennzeichen.value = getKennungTitle(
      "RK_KFZ_KENNZEICHEN",
      uiData.columns
    );

    fahrtkosten.value = getKennungValue(row, "Fahrtkosten", uiData.columns);
    selectFahrzeug.value = getKennungValueDisplay(
      row,
      "RK_TRANSPORTMITTEL",
      uiData.columns
    );
    km.value = getKennungValue(row, "B_CALC_FIELD", uiData.columns);
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});

async function onValidate() {
  return await validator.validateAsync();
}
async function save() {
  const isvalid = await onValidate();
  if (!isvalid.valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" +
        isvalid.invalidFields.join(", ") +
        ")",
      duration: 4000,
      color: "error",
    });
    return;
  }
  const siteData = getSiteData();
  const aicReise = siteData?.aicReise;

  onSave(uiData, pageParams, 0, "", undefined, aicReise)?.then(() => {
    emit("back");
  });
}
async function bewDelete() {
  if (uiData.data && uiData.data[0].aic_Bew_pool) {
    await onDelete(uiData, pageParams, uiData.data[0].aic_Bew_pool, "");
    emit("back");
  }
}
function checkValue(evt: any) {
  uiData.initialized = false;
  selectFahrzeug.value = true;
  if (evt.value && evt.value.setShow !== undefined) {
    fahrtkosten.value = evt.value.setShow;
    componentKey.value += 1;
  }
  uiData.initialized = true;
}
function setKm(selected: number) {
  uiData.initialized = false;
  if (selected) {
    km.value = selected - abfahrt.value;
  }
  setCompKey.value += +1;
  uiData.initialized = true;
}
function setKmAbfahrt(selected: number) {
  uiData.initialized = false;
  if (selected) {
    km.value = ankunft.value - selected;
  }
  setCompKey.value += +1;
  uiData.initialized = true;
}
</script>
<template>
  <v-card :loading="uiData.showLoading" class="noPaddingNoMargin">
    <v-card-text v-if="uiData.initialized">
      <EditToggleSight
        kennung="RK_TRANSPORTMITTEL"
        :queryZeitbereich="pageParams.zeitbereich"
        v-model="selectFahrzeug"
        @seteditvalue="checkValue"
      />

      <v-row v-if="selectFahrzeug && fahrtkosten" class="pl-0 pt-10">
        <v-col cols="12" sm="12">
          <EditSlider
            :disabled="!selectFahrzeug"
            class="pt-2 pr-5"
            kennung="RK_BEIFAHRER"
          />
        </v-col>
      </v-row>
      <v-row v-if="selectFahrzeug && fahrtkosten" class="pa-0">
        <v-col cols="12" sm="12">
          <EditZahl
            v-if="showAbfahrt"
            v-model="abfahrt"
            kennung="RK_KILOMETER"
            @input="setKmAbfahrt"
          />
          <EditZahl
            v-if="showAbfahrt"
            v-model="ankunft"
            kennung="RK_KM STAND ANKUNFT"
            @input="setKm"
          />
          <EditZahl
            :key="setCompKey"
            :disabled="true"
            :initValue="km"
            v-if="showAbfahrt"
            kennung="B_CALC_FIELD"
          />
        </v-col>
      </v-row>
      <EditZahl v-if="!showAbfahrt" kennung="RK_KM_STAND_ANKUNFT" />
      <EditTextField v-if="showKennzeichen" kennung="RK_KFZ_KENNZEICHEN" />
      <EditTextArea kennung="BEMERKUNG" />
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <CommonBaseButtons
        :deleteVisible="showDeleteMask"
        :createVisible="false"
        :refreshVisible="false"
        :backVisible="false"
        :saveVisible="true"
        @delete="bewDelete"
        @back="goBack()"
        @save="save"
      ></CommonBaseButtons>
    </v-card-actions>
  </v-card>
</template>
<style scoped>
.dvhCol {
  padding-right: 10px;
  padding-left: 10px;
  padding-top: 0px;
  padding-bottom: 0px;
}
</style>
